import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { useCareerAndSeasonStatsNavSync } from './hooks/use-career-and-season-stats-nav-sync';
import { CategoryNavItem, SeasonNavItem } from './components/nav-items';
import { CareerAndSeasonStatsTable } from './components/table';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import {
    careerAndSeasonStatsBenchmarkingSelector,
    careerAndSeasonStatsDataTypeSelector,
    careerAndSeasonStatsNavSelector
} from 'store/modules/statspro/career-and-season-stats/selectors';
import {
    BenchmarkingLegend,
    BenchmarkingExplainer
} from 'common/react/components/benchmarking';
import {
    BenchmarkingSwitch,
    dataTypeOptions,
    DataTypeToggle,
    Heading
} from './components/header';
import { useGetCompSeasonQuery } from 'common/store/modules/api/afl-api';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { UPDATE_NAV_ITEM } from 'store/modules/statspro/career-and-season-stats/actions';
import { getTranslation } from 'common/react/utils/translations';

const NavWrapper = ({ children }) => (
    <div className="competition-nav competition-nav--no-margin-top filter-dropdown">
        {children}
    </div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const CareerAndSeasonStats = ({
    title,
    compseasonId,
    playerProviderId,
    showBenchmarkingAflw,
    hideClubLogos
}) => {
    const isAFLW = PULSE.app.common.url.checkBaseRoute('aflw');

    const { data: compData } = useGetCompSeasonQuery(
        { id: compseasonId },
        {
            skip: !compseasonId
        }
    );

    const windowSize = useWindowSize();
    const dispatch = useDispatch();
    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const benchmarking = useSelector(careerAndSeasonStatsBenchmarkingSelector);
    const nav = useSelector(careerAndSeasonStatsNavSelector);
    const dataType = useSelector(careerAndSeasonStatsDataTypeSelector);
    const options = useCompetitionNavigationOptions(nav);

    useCareerAndSeasonStatsNavSync(
        options,
        compData?.competition.id,
        compData?.providerId
    );

    const showBenchmarkingAFLWBool = showBenchmarkingAflw === 'true';
    const showBenchmarking = !isAFLW || (isAFLW && showBenchmarkingAFLWBool);

    const isAFLM =
        compData?.competition.providerId ===
        PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP;

    const renderAveragesBenchmarking =
        showBenchmarking &&
        (dataType.type === 'averages' ||
            dataType.type === 'benchmarkedAverages');

    const careerAndSeasonStatsRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            category: nav.category,
            season: nav.season,
            dataType,
            benchmarking
        },
        options: {
            ...options,
            dataTypes: dataTypeOptions
        },
        filtersFields: [
            'category',
            'career-season',
            'benchmarking',
            'dataType'
        ],
        filtersRef: careerAndSeasonStatsRef
    });

    const getInitialCategoryId = () => {
        return 'Key Stats'; // Default category from initial state
    };

    const getInitialSeasonId = () => {
        // If compseasonId is provided, that's the intended initial season
        // Otherwise, default to -1 (career view) which is the reducer's initial state
        return compseasonId ? parseInt(compseasonId, 10) : -1;
    };

    const hasChangedFromDefaults = () => {
        if (!nav?.category || !nav?.season) {
            return false;
        }

        const initialCategoryId = getInitialCategoryId();
        const initialSeasonId = getInitialSeasonId();

        // Only check for changes if we have valid initial values
        if (
            !initialCategoryId ||
            (initialSeasonId !== -1 && !initialSeasonId)
        ) {
            return false;
        }

        const isCategoryChanged = nav?.category !== initialCategoryId;
        const isSeasonChanged = nav?.season?.id !== initialSeasonId;

        return isCategoryChanged || isSeasonChanged;
    };

    const resetToDefaults = () => {
        const initialCategoryId = getInitialCategoryId();
        const initialSeasonId = getInitialSeasonId();

        if (nav?.category !== initialCategoryId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'category',
                    value: initialCategoryId
                })
            );
        }
        if (nav?.season?.id !== initialSeasonId) {
            // Only reset season if we have a valid initial season ID
            if (
                initialSeasonId !== null &&
                typeof initialSeasonId !== 'undefined'
            ) {
                dispatch(
                    UPDATE_NAV_ITEM.request({
                        navItem: 'season',
                        value: { id: initialSeasonId }
                    })
                );
            }
        }
    };

    return (
        <div ref={careerAndSeasonStatsRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Heading title={title} />
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <CategoryNavItem hideLabel />
                        <SeasonNavItem classes="career-and-season-season-nav-item" />
                        {hasChangedFromDefaults() && !showResponsiveItems ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults() && showResponsiveItems ? (
                        <StatsSection.NavFilterItems>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentHeader>
                        <StatsSection.ContentHeaderHeading>
                            <Heading />
                        </StatsSection.ContentHeaderHeading>
                        {nav.season.id === -1 && (
                            <StatsSection.ContentHeaderOptions>
                                {renderAveragesBenchmarking && (
                                    <BenchmarkingSwitch />
                                )}
                                <DataTypeToggle />
                            </StatsSection.ContentHeaderOptions>
                        )}
                        <StatsSection.ContentHeaderBelow>
                            {benchmarking?.isActive && <BenchmarkingLegend />}
                            {benchmarking?.isActive && (
                                <BenchmarkingExplainer />
                            )}
                        </StatsSection.ContentHeaderBelow>
                    </StatsSection.ContentHeader>
                    <StatsSection.ContentBody>
                        <CareerAndSeasonStatsTable
                            isAFLW={isAFLW}
                            isAFLM={isAFLM}
                            hideClubLogos={
                                hideClubLogos === 'true' ? true : false
                            }
                            playerId={playerProviderId}
                        />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

CareerAndSeasonStats.propTypes = {
    title: PropTypes.string.isRequired,
    compseasonId: PropTypes.string.isRequired,
    playerProviderId: PropTypes.string.isRequired,
    showBenchmarkingAflw: PropTypes.string,
    hideClubLogos: PropTypes.string
};
